import React, { useState, useEffect } from 'react';
import supabase from '../supabaseClientFixed';
import { Loader2, X, AlertCircle, CheckCircle, User, Calendar, Globe, School, Building, ChevronRight } from 'lucide-react';

const CompleteProfile = ({ user, onComplete, onSkip }) => {
  const [formData, setFormData] = useState({
    full_name: user?.user_metadata?.full_name || '',
    fecha_nacimiento: '',
    pais: 'Perú',
    tipo_usuario: '',
    año_estudio: '',
    universidad: '',
    universidad_otra: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [universities, setUniversities] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);

  useEffect(() => {
    fetchUniversities();
  }, []);

  const fetchUniversities = async () => {
    try {
      const { data, error } = await supabase
        .from('universities_peru')
        .select('nombre')
        .order('orden', { ascending: true });

      if (error) throw error;
      setUniversities(data?.map(u => u.nombre) || []);
    } catch (error) {
      console.error('Error fetching universities:', error);
      // Lista de respaldo
      setUniversities([
        'Universidad Nacional Mayor de San Marcos',
        'Universidad Peruana Cayetano Heredia',
        'Universidad de San Martín de Porres',
        'Universidad Ricardo Palma',
        'Universidad Nacional Federico Villarreal',
        'Universidad Peruana de Ciencias Aplicadas',
        'Universidad Católica de Santa María',
        'Universidad Nacional de Trujillo',
        'Universidad Nacional Pedro Ruiz Gallo',
        'Universidad Nacional San Antonio Abad',
        'Universidad Nacional del Altiplano',
        'Universidad Nacional de San Agustín',
        'Universidad Privada Antenor Orrego',
        'Universidad César Vallejo',
        'Universidad Católica Santo Toribio de Mogrovejo',
        'Universidad Nacional de Piura',
        'Universidad Peruana Los Andes',
        'Universidad Continental',
        'Universidad San Juan Bautista',
        'Universidad Científica del Sur',
        'Otra'
      ]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validación final
    if (!formData.fecha_nacimiento || !formData.tipo_usuario || !formData.universidad) {
      setError('Por favor completa todos los campos obligatorios');
      return;
    }

    if (formData.tipo_usuario === 'estudiante' && !formData.año_estudio) {
      setError('Por favor indica tu año de estudio');
      return;
    }

    if (formData.universidad === 'Otra' && !formData.universidad_otra) {
      setError('Por favor especifica el nombre de tu universidad');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('Iniciando guardado de perfil para usuario:', user.id, user.email);
      
      // 1. Primero verificar si el perfil ya existe
      const { data: existingProfile, error: checkError } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();
      
      if (checkError && checkError.code !== 'PGRST116') {
        // PGRST116 significa que no se encontró registro, que es lo esperado
        console.error('Error verificando perfil existente:', checkError);
        throw new Error(`Error verificando perfil: ${checkError.message}`);
      }
      
      if (existingProfile) {
        console.log('El perfil ya existe, actualizando...');
        // Si ya existe, actualizar en lugar de insertar
        const { error: updateError } = await supabase
          .from('user_profiles')
          .update({
            full_name: formData.full_name || user.email.split('@')[0],
            fecha_nacimiento: formData.fecha_nacimiento,
            pais: formData.pais,
            tipo_usuario: formData.tipo_usuario,
            año_estudio: formData.tipo_usuario === 'estudiante' ? parseInt(formData.año_estudio) : null,
            universidad: formData.universidad,
            universidad_otra: formData.universidad === 'Otra' ? formData.universidad_otra : null,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', user.id);
        
        if (updateError) {
          console.error('Error actualizando perfil:', updateError);
          throw new Error(`Error actualizando perfil: ${updateError.message}`);
        }
      } else {
        console.log('Creando nuevo perfil...');
        // Crear nuevo perfil
        const profileData = {
          user_id: user.id,
          full_name: formData.full_name || user.email.split('@')[0],
          fecha_nacimiento: formData.fecha_nacimiento,
          pais: formData.pais,
          tipo_usuario: formData.tipo_usuario,
          año_estudio: formData.tipo_usuario === 'estudiante' ? parseInt(formData.año_estudio) : null,
          universidad: formData.universidad,
          universidad_otra: formData.universidad === 'Otra' ? formData.universidad_otra : null,
          trial_start_date: new Date().toISOString(),
          trial_end_date: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
          is_trial_active: true,
          subscription_status: 'trial'
        };
        
        console.log('Datos del perfil a insertar:', profileData);
        
        const { data: insertedProfile, error: profileError } = await supabase
          .from('user_profiles')
          .insert([profileData])
          .select();
        
        if (profileError) {
          console.error('Error detallado al insertar perfil:', profileError);
          if (profileError.code === '42P01') {
            throw new Error('La tabla user_profiles no existe. Por favor ejecuta la migración en Supabase.');
          } else if (profileError.code === '42501') {
            throw new Error('Sin permisos para crear perfil. Verifica las políticas RLS en Supabase.');
          } else if (profileError.code === '23505') {
            throw new Error('Ya existe un perfil para este usuario.');
          } else {
            throw new Error(`Error al crear perfil: ${profileError.message || profileError}`);
          }
        }
        
        console.log('Perfil creado exitosamente:', insertedProfile);
      }

      // 1.5. Registrar email en la tabla user_emails para el panel admin
      try {
        await supabase.rpc('register_user_email', {
          p_user_id: user.id,
          p_email: user.email
        });
        console.log('Email registrado para panel admin');
      } catch (emailRegError) {
        console.log('No se pudo registrar email (función puede no existir):', emailRegError);
      }

      // 2. Enviar email de bienvenida
      try {
        await fetch('/.netlify/functions/send-welcome-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: user.email,
            name: formData.full_name || user.email.split('@')[0]
          })
        });
      } catch (emailError) {
        console.error('Error enviando email:', emailError);
      }

      // 3. Actualizar metadatos del usuario
      const { error: updateError } = await supabase.auth.updateUser({
        data: {
          profile_completed: true,
          full_name: formData.full_name,
          universidad: formData.universidad,
          profile_created_at: new Date().toISOString()
        }
      });
      
      if (updateError) {
        console.error('Error actualizando metadatos:', updateError);
      }
      
      // 4. Forzar refresco de la sesión para persistir cambios
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.refreshSession();
        if (sessionError) {
          console.error('Error refrescando sesión:', sessionError);
        } else {
          console.log('Sesión refrescada exitosamente');
        }
      } catch (refreshError) {
        console.error('Error en refresh:', refreshError);
      }

      console.log('Perfil completado exitosamente');
      
      // 5. Guardar en localStorage como respaldo
      localStorage.setItem('profile_completed_' + user.id, 'true');
      
      onComplete && onComplete();
    } catch (error) {
      console.error('Error completando perfil:', error);
      // Mostrar el mensaje de error específico
      const errorMessage = error.message || 'Error al guardar tu perfil. Por favor intenta nuevamente.';
      setError(errorMessage);
      
      // Si es un error de tabla no existente, mostrar instrucciones adicionales
      if (error.message && error.message.includes('tabla user_profiles no existe')) {
        console.error('IMPORTANTE: Necesitas ejecutar la migración SQL en Supabase.');
        console.error('Ve a Supabase Dashboard > SQL Editor y ejecuta el archivo:');
        console.error('supabase-migrations/001_user_profiles_system.sql');
      }
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (currentStep === 1 && !formData.full_name) {
      setError('Por favor ingresa tu nombre completo');
      return;
    }
    if (currentStep === 2 && (!formData.fecha_nacimiento || !formData.pais)) {
      setError('Por favor completa todos los campos');
      return;
    }
    setError('');
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setError('');
    setCurrentStep(currentStep - 1);
  };

  const calcularEdad = (fecha) => {
    const hoy = new Date();
    const nacimiento = new Date(fecha);
    let edad = hoy.getFullYear() - nacimiento.getFullYear();
    const mes = hoy.getMonth() - nacimiento.getMonth();
    if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
      edad--;
    }
    return edad;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 rounded-t-2xl">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">
                Completa tu Perfil
              </h2>
              <p className="text-blue-100 text-sm">
                Necesitamos algunos datos para personalizar tu experiencia
              </p>
            </div>
            {onSkip && (
              <button
                onClick={onSkip}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            )}
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex justify-between mb-2">
              {[1, 2, 3].map((step) => (
                <div
                  key={step}
                  className={`flex-1 h-2 rounded-full mx-1 ${
                    step <= currentStep
                      ? 'bg-white'
                      : 'bg-white/30'
                  }`}
                />
              ))}
            </div>
            <p className="text-white text-xs text-center">
              Paso {currentStep} de 3
            </p>
          </div>
        </div>

        {/* Trial Info */}
        <div className="bg-green-50 dark:bg-green-900/20 p-4 border-b border-green-200 dark:border-green-800">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
            <p className="text-sm text-green-800 dark:text-green-300">
              <strong>¡3 días de prueba GRATIS!</strong> Con 20 preguntas por examen
            </p>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
            </div>
          </div>
        )}

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Step 1: Nombre */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <User className="h-12 w-12 text-blue-600 mx-auto mb-2" />
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                  ¿Cómo te llamas?
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Tu nombre aparecerá en tus certificados
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nombre Completo *
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-lg"
                  placeholder="Ej: Juan Pérez García"
                  required
                />
              </div>

              <button
                type="button"
                onClick={nextStep}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all flex items-center justify-center gap-2"
              >
                Continuar
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          )}

          {/* Step 2: Datos Personales */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <Calendar className="h-12 w-12 text-blue-600 mx-auto mb-2" />
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                  Datos Personales
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Información básica para tu perfil
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Fecha de Nacimiento *
                </label>
                <input
                  type="date"
                  value={formData.fecha_nacimiento}
                  onChange={(e) => setFormData({ ...formData, fecha_nacimiento: e.target.value })}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  required
                />
                {formData.fecha_nacimiento && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Edad: {calcularEdad(formData.fecha_nacimiento)} años
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <Globe className="inline h-4 w-4 mr-1" />
                  País *
                </label>
                <select
                  value={formData.pais}
                  onChange={(e) => setFormData({ ...formData, pais: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  required
                >
                  <option value="Perú">Perú</option>
                  <option value="Colombia">Colombia</option>
                  <option value="Ecuador">Ecuador</option>
                  <option value="Bolivia">Bolivia</option>
                  <option value="Chile">Chile</option>
                  <option value="Argentina">Argentina</option>
                  <option value="Venezuela">Venezuela</option>
                  <option value="México">México</option>
                  <option value="España">España</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={prevStep}
                  className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white py-3 px-4 rounded-lg font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition-all"
                >
                  Atrás
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all flex items-center justify-center gap-2"
                >
                  Continuar
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Datos Académicos */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <School className="h-12 w-12 text-blue-600 mx-auto mb-2" />
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                  Información Académica
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Cuéntanos sobre tu formación médica
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  ¿Eres médico o estudiante? *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, tipo_usuario: 'medico', año_estudio: '' })}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      formData.tipo_usuario === 'medico'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                    }`}
                  >
                    <div className="font-semibold">Médico</div>
                    <div className="text-xs opacity-75">Ya titulado</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, tipo_usuario: 'estudiante' })}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      formData.tipo_usuario === 'estudiante'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                    }`}
                  >
                    <div className="font-semibold">Estudiante</div>
                    <div className="text-xs opacity-75">En formación</div>
                  </button>
                </div>
              </div>

              {formData.tipo_usuario === 'estudiante' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Año de Estudio *
                  </label>
                  <select
                    value={formData.año_estudio}
                    onChange={(e) => setFormData({ ...formData, año_estudio: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    required
                  >
                    <option value="">Selecciona tu año</option>
                    {[1, 2, 3, 4, 5, 6, 7].map(year => (
                      <option key={year} value={year}>
                        {year}° año
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <Building className="inline h-4 w-4 mr-1" />
                  Universidad *
                </label>
                <select
                  value={formData.universidad}
                  onChange={(e) => setFormData({ ...formData, universidad: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  required
                >
                  <option value="">Selecciona tu universidad</option>
                  {universities.map((uni) => (
                    <option key={uni} value={uni}>{uni}</option>
                  ))}
                </select>
              </div>

              {formData.universidad === 'Otra' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Especifica tu Universidad *
                  </label>
                  <input
                    type="text"
                    value={formData.universidad_otra}
                    onChange={(e) => setFormData({ ...formData, universidad_otra: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Nombre de tu universidad"
                    required
                  />
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={prevStep}
                  className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white py-3 px-4 rounded-lg font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition-all"
                >
                  Atrás
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-green-600 to-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-green-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      Completar Registro
                      <CheckCircle className="h-5 w-5" />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="px-6 pb-6">
          <p className="text-xs text-center text-gray-500 dark:text-gray-400">
            Al completar tu perfil, aceptas nuestros{' '}
            <a href="/terms" className="text-blue-600 hover:underline">
              términos y condiciones
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CompleteProfile;